import { clamp, saturate, signedWrappedDelta } from "../physics/numbers";
import type { ImpactSide, VehicleState } from "./types";

export const VEHICLE_CONTACT_LENGTH = 4.7;
export const VEHICLE_CONTACT_WIDTH = 2.08;

export type VehicleContactResult = {
  collided: boolean;
  longitudinalDistance: number;
  lateralOverlap: number;
  speedImpulse: number;
};

const NO_CONTACT: VehicleContactResult = {
  collided: false,
  longitudinalDistance: 0,
  lateralOverlap: 0,
  speedImpulse: 0
};

const getSeparationDirection = (first: VehicleState, second: VehicleState): -1 | 1 => {
  const lateralDelta = first.lateralOffset - second.lateralOffset;

  if (Math.abs(lateralDelta) > 0.001) {
    return lateralDelta > 0 ? 1 : -1;
  }

  return first.id.localeCompare(second.id) <= 0 ? -1 : 1;
};

const separateLateralOverlap = (
  first: VehicleState,
  second: VehicleState,
  roadHalfWidth: number
): number => {
  const lateralDistance = Math.abs(first.lateralOffset - second.lateralOffset);

  if (lateralDistance >= VEHICLE_CONTACT_WIDTH) {
    return 0;
  }

  const lateralOverlap = VEHICLE_CONTACT_WIDTH - lateralDistance;
  const separationDirection = getSeparationDirection(first, second);
  const halfSeparation = VEHICLE_CONTACT_WIDTH * 0.5;
  const boundedMidpoint = clamp(
    (first.lateralOffset + second.lateralOffset) * 0.5,
    -roadHalfWidth + halfSeparation,
    roadHalfWidth - halfSeparation
  );
  first.lateralOffset = clamp(
    boundedMidpoint + separationDirection * halfSeparation,
    -roadHalfWidth,
    roadHalfWidth
  );
  second.lateralOffset = clamp(
    boundedMidpoint - separationDirection * halfSeparation,
    -roadHalfWidth,
    roadHalfWidth
  );
  return lateralOverlap;
};

const applyContactFeedback = (
  vehicle: VehicleState,
  impactSide: ImpactSide,
  intensity: number
): void => {
  vehicle.feedback.impact = Math.max(vehicle.feedback.impact, intensity);
  vehicle.feedback.impactSide = impactSide;
  vehicle.impactCooldown = Math.max(vehicle.impactCooldown, 0.22);
};

const applySpeedTransfer = (
  first: VehicleState,
  second: VehicleState,
  longitudinalDistance: number
): number => {
  let trailing = longitudinalDistance >= 0 ? first : second;
  let leading = longitudinalDistance >= 0 ? second : first;

  if (Math.abs(longitudinalDistance) < 0.05 && leading.speed > trailing.speed) {
    [trailing, leading] = [leading, trailing];
  }

  const closingSpeed = Math.max(0, trailing.speed - leading.speed);
  const speedImpulse = clamp(closingSpeed * 0.38, 0, 6);

  if (speedImpulse === 0) {
    return 0;
  }

  trailing.speed = Math.max(0, trailing.speed - speedImpulse);
  leading.speed = Math.min(115, leading.speed + speedImpulse * 0.45);
  trailing.acceleration = Math.min(trailing.acceleration, -speedImpulse * 8);
  leading.acceleration = Math.max(leading.acceleration, speedImpulse * 3.6);
  return speedImpulse;
};

export const resolveVehicleContact = (
  first: VehicleState,
  second: VehicleState,
  trackLength: number,
  roadHalfWidth: number
): VehicleContactResult => {
  if (
    first.id === second.id ||
    !Number.isFinite(trackLength) ||
    trackLength <= 0 ||
    !Number.isFinite(roadHalfWidth) ||
    roadHalfWidth <= 0
  ) {
    return { ...NO_CONTACT };
  }

  const longitudinalDistance =
    signedWrappedDelta(first.trackProgress, second.trackProgress) * trackLength;

  if (Math.abs(longitudinalDistance) >= VEHICLE_CONTACT_LENGTH) {
    return { ...NO_CONTACT, longitudinalDistance };
  }

  const lateralOverlap = separateLateralOverlap(first, second, roadHalfWidth);

  if (lateralOverlap === 0) {
    return { ...NO_CONTACT, longitudinalDistance };
  }

  const separationDirection = getSeparationDirection(first, second);

  const overlapRatio = saturate(lateralOverlap / VEHICLE_CONTACT_WIDTH);
  const lateralImpulse = 0.75 + overlapRatio * 2.25;
  first.lateralVelocity = clamp(
    first.lateralVelocity + separationDirection * lateralImpulse,
    -12,
    12
  );
  second.lateralVelocity = clamp(
    second.lateralVelocity - separationDirection * lateralImpulse,
    -12,
    12
  );

  const speedImpulse = applySpeedTransfer(first, second, longitudinalDistance);
  const intensity = clamp(0.36 + overlapRatio * 0.3 + speedImpulse * 0.055, 0.36, 1);
  const oppositeImpactSide: ImpactSide = separationDirection === 1 ? -1 : 1;
  applyContactFeedback(first, separationDirection, intensity);
  applyContactFeedback(second, oppositeImpactSide, intensity);

  return {
    collided: true,
    longitudinalDistance,
    lateralOverlap,
    speedImpulse
  };
};

export const resolveVehicleContacts = (
  vehicles: readonly VehicleState[],
  trackLength: number,
  roadHalfWidth: number
): number => {
  let contactCount = 0;

  for (let firstIndex = 0; firstIndex < vehicles.length; firstIndex += 1) {
    const first = vehicles[firstIndex];

    if (first === undefined) {
      continue;
    }

    for (let secondIndex = firstIndex + 1; secondIndex < vehicles.length; secondIndex += 1) {
      const second = vehicles[secondIndex];

      if (second === undefined) {
        continue;
      }

      if (resolveVehicleContact(first, second, trackLength, roadHalfWidth).collided) {
        contactCount += 1;
      }
    }
  }

  const relaxationPasses = Math.max(4, vehicles.length * vehicles.length);

  for (let pass = 1; pass < relaxationPasses; pass += 1) {
    let correctedOverlap = false;

    for (let firstIndex = 0; firstIndex < vehicles.length; firstIndex += 1) {
      const first = vehicles[firstIndex];

      if (first === undefined) {
        continue;
      }

      for (let secondIndex = firstIndex + 1; secondIndex < vehicles.length; secondIndex += 1) {
        const second = vehicles[secondIndex];

        if (second === undefined) {
          continue;
        }

        const longitudinalDistance =
          signedWrappedDelta(first.trackProgress, second.trackProgress) * trackLength;

        if (Math.abs(longitudinalDistance) >= VEHICLE_CONTACT_LENGTH) {
          continue;
        }

        correctedOverlap =
          separateLateralOverlap(first, second, roadHalfWidth) > 0 || correctedOverlap;
      }
    }

    if (!correctedOverlap) {
      break;
    }
  }

  return contactCount;
};
